import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _isNil from 'lodash/isNil';

import { bem, classNameWithModifiers as withMods } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { getVenueClassPayoutRate } from 'components/ClassesList/ClassesList.helpers';
import { selectActiveVenue, selectRouteParams } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import ServicesContext from '../ServicesContext';
import LegacyServicesListItemContent from './LegacyServicesListItemContent';
import PricingServicesListItemContent from './PricingServicesListItemContent';

import './ServicesListItem.scss';

const { block, element } = bem('services-list-item');

const ServicesListItem = ({
  id,
  name,
  duration,
  practitioners,
  resources,
  disabled,
  classPricingRule,
  price,
  payout,
  retailRateRequest,
  status,
  showPricingColumns,
}) => {
  const navigate = useNavigate();
  const routeParams = useSelector(selectRouteParams);
  const isMobile = useSelector(selectIsMobile);
  const { currencyCode } = useSelector(selectActiveVenue);
  const { selectService, selectedService } = useContext(ServicesContext);
  const practitionersLength = practitioners?.length;
  const equipmentLength = resources?.length;
  const isSelected = id === selectedService?.id;
  const isDisabled = !practitionersLength && !equipmentLength;
  const hasPrice = !_isNil(price);
  const payoutRate = getVenueClassPayoutRate({ payout }, currencyCode);
  const hasPayout = !_isNil(payoutRate);
  const service = { id, disabled, classPricingRule, price, payout, retailRateRequest, status };

  const metadataProps = {
    duration,
    equipmentLength,
    isDisabled,
    name,
    practitionersLength,
  };

  function selectServiceAndNavigate() {
    selectService(id);

    const newPath = makePath.manageServices({
      ...routeParams,
      itemId: id,
      focus: isMobile ? 'details' : 'list',
    });

    navigate(newPath);
  }

  const listItemClassName = withMods(block(), {
    isDisabled: showPricingColumns ? isDisabled : isDisabled || !hasPrice,
  });

  return (
    <li className={listItemClassName}>
      {isSelected && <span className={element('selected-marker')} />}
      <button type="button" className={element('button')} onClick={selectServiceAndNavigate}>
        {showPricingColumns ? (
          <PricingServicesListItemContent
            {...metadataProps}
            currencyCode={currencyCode}
            hasPayout={hasPayout}
            payoutRate={payoutRate}
            service={service}
          />
        ) : (
          <LegacyServicesListItemContent {...metadataProps} hasPrice={hasPrice} />
        )}
      </button>
    </li>
  );
};

ServicesListItem.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  duration: PropTypes.string,
  disabled: PropTypes.bool,
  price: PropTypes.number,
  status: PropTypes.string,
  showPricingColumns: PropTypes.bool,
  payout: PropTypes.shape({
    minimumPayout: PropTypes.shape({
      currencyCode: PropTypes.string,
      currencyUnits: PropTypes.number,
    }),
  }),
  retailRateRequest: PropTypes.shape({
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    statusReason: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  classPricingRule: PropTypes.shape({
    status: PropTypes.string,
  }),
  practitioners: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.number })),
  resources: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.number })),
};

export default ServicesListItem;
